import { ErrorType } from "@/graphql";

/**
 * Ensures a non-null array of defined elements.
 *
 * Note: this function is a bit of a hack to get around the fact that all our
 *       backend arrays have nullable elements by default. Eventually we should
 *       handle nulls more explicitly and remove this function.
 *
 * This function handles two cases:
 * 1. When the input array itself is undefined or null, it returns an empty array
 * 2. When the input array contains null elements, it filters them out
 *
 * @example
 * // Returns an empty array
 * ensureNonNullArray(undefined);
 *
 * @example
 * // Returns an empty array
 * ensureNonNullArray(null);
 *
 * @example
 * // Returns ['a', 'c']
 * ensureNonNullArray(['a', null, 'c', null]);
 */

// Cache for empty array to avoid creating new instances
const EMPTY_ARRAY: readonly unknown[] = [];

export function ensureNonNullArray<T>(
  items: (T | null)[] | null | undefined,
): T[] {
  // Return cached empty array for null/undefined inputs
  if (!items) return EMPTY_ARRAY as T[];

  // Check if any items are null - if not, return original array
  const hasNullItems = items.some((item) => item === null);
  if (!hasNullItems) {
    // Safe to cast since we confirmed no nulls exist
    return items as T[];
  }

  // Only filter when we actually have null items
  return items.filter((item): item is T => item !== null);
}

/**
 * Extracts GraphQL error messages as comma-separated string
 * Handles both 'message' (singular) and 'messages' (array) fields
 *
 * @param errors - The errors to format
 * @returns A comma-separated string of error messages
 */
export function formatErrors(
  errors?: (ErrorType | null)[] | null | undefined,
): string {
  if (!errors || errors.length === 0) return "";

  const allMessages: string[] = [];

  errors.forEach((error) => {
    if (!error) return;

    // Handle singular message field
    if (error.message) {
      allMessages.push(error.message);
    }

    // Handle messages array field
    if (error.messages && Array.isArray(error.messages)) {
      allMessages.push(
        ...error.messages.filter(
          (message): message is string => message !== null,
        ),
      );
    }
  });

  return allMessages.join(", ");
}
